import dayjs from "dayjs";
import { routeCalculationService } from "@/services/RouteCalculationService";
import { RoutePointMapper } from "@/adapters/RoutePoint.mapper";
import { Base2RoutePointCreateData } from "@/adapters/Base2RoutePointTransferData.mapper";
import { OrderToRoutePointCreateData } from "@/adapters/OrderToRoutePointTransferData.mapper";
/**
 * Найти оптимальную базу для заказа
 * Рассчитывает время маршрута от каждой базы через edge function route-calculation
 * Возвращает базу с минимальным временем пути и предрассчитанными точками с геометрией
 */
export async function findOptimalBase(order, bases) {
    if (!bases || bases.length === 0) {
        throw new Error("Нет доступных баз для построения маршрута");
    }
    const baseMapper = new Base2RoutePointCreateData();
    const orderMapper = new OrderToRoutePointCreateData();
    // Рассчитываем время от каждой базы до заказа
    const promises = bases.map(async (base) => {
        try {
            const basePoint = RoutePointMapper.toCreateDto(baseMapper.adapt(base));
            const orderPoint = RoutePointMapper.toCreateDto(orderMapper.adapt(order));
            const calculatedPoints = await routeCalculationService.calculateRoutePointTimes(dayjs().toISOString(), [basePoint, orderPoint]);
            const baseResult = calculatedPoints[0];
            const orderResult = calculatedPoints[1];
            if (baseResult.plannedDepartureTime && orderResult.plannedArrivalTime) {
                const start = dayjs(baseResult.plannedDepartureTime);
                const end = dayjs(orderResult.plannedArrivalTime);
                const duration = end.diff(start, 'seconds');
                return { base, duration };
            }
            return null;
        }
        catch (e) {
            console.error(`[routeOptimization] Calculation failed for base ${base.id}`, e);
            return null;
        }
    });
    const results = await Promise.all(promises);
    const validResults = results.filter((r) => r !== null);
    if (validResults.length === 0) {
        throw new Error("Не удалось рассчитать маршруты ни от одной базы");
    }
    // Сортируем по времени и выбираем лучшую
    validResults.sort((a, b) => a.duration - b.duration);
    const bestBase = validResults[0].base;
    const bestDurationSeconds = validResults[0].duration;
    // Рассчитываем время старта маршрута
    const now = dayjs();
    const orderTime = dayjs(order.scheduledDate);
    let calculatedStartTime = orderTime.subtract(bestDurationSeconds, 'seconds');
    // Если рассчитанное время в прошлом, используем текущее
    if (calculatedStartTime.isBefore(now)) {
        calculatedStartTime = now;
    }
    // Финальный вызов с полным маршрутом [base → order → return_base] и withGeometry: true
    // Этот один вызов заменяет повторный расчёт на странице маршрута
    const bestBasePoint = RoutePointMapper.toCreateDto(baseMapper.adapt(bestBase));
    const orderPoint = RoutePointMapper.toCreateDto(orderMapper.adapt(order));
    const returnBasePoint = RoutePointMapper.toCreateDto(baseMapper.adapt(bestBase));
    const precalculatedPoints = await routeCalculationService.calculateRoutePointTimes(calculatedStartTime.toISOString(), [bestBasePoint, orderPoint, returnBasePoint], true // withGeometry: true — получаем геометрию сегментов для карты
    );
    return {
        base: bestBase,
        duration: bestDurationSeconds,
        startTime: calculatedStartTime,
        precalculatedPoints,
    };
}
